/**
 * `<DataGrid>` — the one Attio-style ruled table used across every dashboard
 * list/grid surface (Contacts, Channels, Knowledge, Tables, Members, …).
 *
 * The Storybook `dashboard/final` fixtures hand-roll the same grid in a dozen
 * places: an edge-to-edge bordered table with a quiet `bg-muted` header,
 * hairline column dividers, compact `13px` rows, and a soft hover tint. This is
 * the single dashboard-side source for that treatment so the look can't drift.
 *
 * Wrap the shadcn `Table` primitives:
 *   <DataGrid>
 *     <TableHeader>
 *       <DataGridHeaderRow>
 *         <DataGridHead className="w-[200px] pl-6 pr-3">Name</DataGridHead>
 *         <DataGridHead className="w-[72px] pr-6 text-right" divider={false}>…</DataGridHead>
 *     <TableBody>
 *       <DataGridRow className="cursor-pointer" onClick={…}>
 *         <DataGridCell className="pl-6 pr-3">…</DataGridCell>
 *
 * `TableHeader` / `TableBody` carry no styling, so import those from
 * `@cometchat-ai/ui-dashboard` as usual. Per-column widths, leading/trailing
 * padding, and alignment are passed via `className` (tailwind-merge wins over
 * the baked-in classes). `divider={false}` drops a column's right hairline —
 * use it on the last/actions column.
 */

import { cn, Table, TableCell, TableHead, TableRow } from "@cometchat-ai/ui-dashboard";
import type { ComponentProps } from "react";

/** Hairline column divider — a 70%-opacity border so the rule reads quieter
 *  than the table frame. Exported for the rare cell that styles itself. */
export const GRID_COL_DIVIDER =
  "border-r border-[color:color-mix(in_oklch,var(--color-border)_70%,transparent)]";

/** Soft row hover tint, matching the Final contacts/channels grid. */
const GRID_ROW_HOVER = "hover:bg-[color:color-mix(in_oklch,var(--color-fg)_2%,transparent)]";

/** The ruled grid table. Edge-to-edge with a bottom frame; `table-fixed` so
 *  column widths come from the `<DataGridHead>` cells. */
export function DataGrid({ className, ...props }: ComponentProps<typeof Table>) {
  return (
    <Table className={cn("table-fixed border-b border-border text-[13px]", className)} {...props} />
  );
}

/** The header row — quiet muted band. */
export function DataGridHeaderRow({ className, ...props }: ComponentProps<typeof TableRow>) {
  return (
    <TableRow
      className={cn("border-b border-border bg-muted hover:bg-muted", className)}
      {...props}
    />
  );
}

/** A header cell. `divider` toggles the trailing column hairline (default on). */
export function DataGridHead({
  className,
  divider = true,
  ...props
}: ComponentProps<typeof TableHead> & { divider?: boolean }) {
  return (
    <TableHead
      className={cn(
        "h-9 px-4 text-xs font-medium text-muted-foreground",
        divider && GRID_COL_DIVIDER,
        className,
      )}
      {...props}
    />
  );
}

/** A body row — adds the `group/row` hook (for hover-reveal children) plus the
 *  bottom hairline and hover tint. Add `cursor-pointer` via `className` when the
 *  row is clickable. */
export function DataGridRow({ className, ...props }: ComponentProps<typeof TableRow>) {
  return (
    <TableRow
      className={cn("group/row border-b border-border", GRID_ROW_HOVER, className)}
      {...props}
    />
  );
}

/** A body cell. `divider` toggles the trailing column hairline (default on).
 *  A fixed `h-11` row height (content vertically centered) keeps every table —
 *  text-only, avatar, or two-line — at the same uniform row height across tabs;
 *  taller cells still grow rather than clip since `height` acts as a minimum. */
export function DataGridCell({
  className,
  divider = true,
  ...props
}: ComponentProps<typeof TableCell> & { divider?: boolean }) {
  return (
    <TableCell
      className={cn(
        "h-11 px-4 py-0 align-middle text-[13px] text-muted-foreground",
        divider && GRID_COL_DIVIDER,
        className,
      )}
      {...props}
    />
  );
}
